import type { WizardReasonWording } from "@/base";
import type { Personnel } from "@/lib/brokerAccounts";
import { isEmailShaped } from "@/lib/email";
import {
  PERSONNEL_ROLE_LABELS,
  type PersonnelRole,
} from "@/lib/personnelRoles";
import { normalizeUsPhone, primaryPhone, withPrimaryNumber } from "@/lib/phone";
import type {
  SubmissionPerson,
  SubmissionPersonInput,
} from "@/lib/submissions/brokerSubmissions";
import {
  CONTACT_ROLES,
  isContactRole,
  isOwnerOrOfficer,
  personAsInput,
  type ContactRole,
} from "@/lib/submissions/wc/people";

// The WC wizard's other contacts (BRK-S337), as pure rules: people who are
// only a contact, below the owners on the People step since BRK-S391. An
// owner's contact roles are ticked on their own row instead. A contact is one
// of the submission's own people holding a contact role (PMS-S740), in the
// same list as the owners and officers, so one person is never entered
// twice. The account's people are never written (Nav, 2026-10-08): an
// account person is copied in. `peopleStep.ts` writes the list.

export { CONTACT_ROLES, type ContactRole };

export const CONTACT_ROLE_OPTIONS = CONTACT_ROLES.map((value) => ({
  value,
  label: PERSONNEL_ROLE_LABELS[value],
}));

/** What a contact is for, as Review shows the step's checkboxes: the ticked
 *  roles' labels in the checkboxes' order (BRK-S349). */
export function contactForText(roles: readonly ContactRole[]): string {
  return CONTACT_ROLE_OPTIONS.filter((o) => roles.includes(o.value))
    .map((o) => o.label)
    .join(", ");
}

export function isContact(p: { roles: readonly PersonnelRole[] }): boolean {
  return p.roles.some(isContactRole);
}

/** Someone who is only a contact: not an owner or officer. */
function isOtherContact(p: { roles: readonly PersonnelRole[] }): boolean {
  return isContact(p) && !isOwnerOrOfficer(p);
}

/** One contact as the step edits them. */
export interface WcContact {
  /** The row's own key: the person's id, or a new row's client key. */
  key: string;
  /** The submission person, when they're already on it. */
  id?: string;
  /** The account person they were copied from, for a copy. */
  sourcePersonnelId?: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Their primary number, as typed; any others are carried. */
  phone: string;
  /** What they're the contact for: one or more of the contact roles. */
  contactRoles: ContactRole[];
}

export function newContact(key: string): WcContact {
  return {
    key,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    contactRoles: [],
  };
}

/** Someone already on the submission (an owner, say) as a contact row: the
 *  same person, who gains a contact role rather than being entered twice. */
export function contactFromPerson(p: SubmissionPerson, key: string): WcContact {
  return {
    key,
    id: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email ?? "",
    phone: primaryPhone(p.phones)?.number ?? "",
    contactRoles: p.roles.filter(isContactRole),
  };
}

/** An account person copied in as a new contact, with their contact roles
 *  only: whether they own or run the business is the People step's to say. */
export function contactFromAccount(p: Personnel, key: string): WcContact {
  return {
    key,
    sourcePersonnelId: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email ?? "",
    phone: primaryPhone(p.phones)?.number ?? "",
    contactRoles: p.roles.filter(isContactRole),
  };
}

/** The other contacts' rows: the submission's people who are only a
 *  contact, in PMS's order. */
export function contactsFrom(
  personnel: readonly SubmissionPerson[],
): WcContact[] {
  return personnel
    .filter(isOtherContact)
    .map((p) => contactFromPerson(p, p.id));
}

/** Who can be added as another contact: the submission's people who are
 *  neither a contact nor an owner (an owner's are ticked on their row), then
 *  the account's people who aren't on the submission yet. */
export function contactCandidates(
  accountPeople: readonly Personnel[],
  personnel: readonly SubmissionPerson[],
  rows: readonly WcContact[],
): { onSubmission: SubmissionPerson[]; onAccount: Personnel[] } {
  const shown = new Set(rows.map((r) => r.id));
  const taken = new Set<string>();
  for (const p of personnel)
    if (p.sourcePersonnelId) taken.add(p.sourcePersonnelId);
  for (const r of rows) if (r.sourcePersonnelId) taken.add(r.sourcePersonnelId);
  return {
    onSubmission: personnel.filter(
      (p) => !isContact(p) && !isOwnerOrOfficer(p) && !shown.has(p.id),
    ),
    onAccount: accountPeople.filter((p) => !taken.has(p.id)),
  };
}

/** A row in write shape: everything else about the person as PMS holds it,
 *  with the row's name, email, primary number and contact roles. */
export function contactInput(
  row: WcContact,
  person?: SubmissionPerson,
): SubmissionPersonInput {
  const input: SubmissionPersonInput = {
    ...(person ? personAsInput(person) : {}),
    firstName: row.firstName.trim(),
    lastName: row.lastName.trim(),
    roles: [
      ...(person?.roles ?? []).filter((r) => !isContactRole(r)),
      ...row.contactRoles,
    ],
    phones: withPrimaryNumber(person?.phones, row.phone),
  };
  const email = row.email.trim();
  if (email) input.email = email;
  else delete input.email;
  if (row.sourcePersonnelId) input.sourcePersonnelId = row.sourcePersonnelId;
  return input;
}

// A person's sentences, said alike by the checks and for PMS's (BRK-S339).
export const FIRST_NAME = "Enter the first name.";
export const LAST_NAME = "Enter the last name.";
export const EMAIL_SHAPE = "Enter a valid email address.";
export const CONTACT_FOR = "Choose what they’re the contact for.";

/** A phone field's words for what PMS's request checks refuse (BRK-S339):
 *  `enter` for a missing number, a US number's own sentence for one too
 *  long (as `normalizeUsPhone` says it), and a plain one otherwise. */
export function phoneWords(enter: string): WizardReasonWording {
  return {
    required: enter,
    too_short: enter,
    too_long: "A US phone number must have 10 digits.",
    invalid: "Enter a valid US phone number.",
  };
}

/** The contacts' checks, keyed as PMS keys its refusals by where each row
 *  lands in the list written (`index`, by row key). A name and what they're
 *  the contact for are needed; an email and a phone are optional, and
 *  checked when given. */
export function validateContacts(
  rows: readonly WcContact[],
  index: Readonly<Record<string, number>>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const r of rows) {
    const at = (field: string) => `personnel.${index[r.key]}.${field}`;
    if (!r.firstName.trim()) errors[at("firstName")] = FIRST_NAME;
    if (!r.lastName.trim()) errors[at("lastName")] = LAST_NAME;
    if (r.email.trim() && !isEmailShaped(r.email)) {
      errors[at("email")] = EMAIL_SHAPE;
    }
    if (r.phone.trim()) {
      const phone = normalizeUsPhone(r.phone);
      if (!phone.ok) errors[at("phones")] = phone.reason;
    }
    if (r.contactRoles.length === 0) {
      errors[at("roles")] = CONTACT_FOR;
    }
  }
  return errors;
}
